import { ChangeEvent, KeyboardEvent, useState } from "react";
import { FaGlobe, FaLink } from "react-icons/fa";

interface DigitalCaseLinksInputProps {
  links: string[];
  onAddLink: (link: string) => void;
  onRemoveLink: (index: number) => void;
  disabled?: boolean;
}

const DigitalCaseLinksInput: React.FC<DigitalCaseLinksInputProps> = ({
  links,
  onAddLink,
  onRemoveLink,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState<string>("");

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleAddLink = () => {
    if (inputValue.trim()) {
      // Add http:// prefix if the URL doesn't have a protocol
      let formattedUrl = inputValue.trim();
      if (
        !formattedUrl.startsWith("http://") &&
        !formattedUrl.startsWith("https://")
      ) {
        formattedUrl = `https://${formattedUrl}`;
      }

      onAddLink(formattedUrl);
      setInputValue("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddLink();
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="font-bold text-[14px] leading-[16.94px] text-black">
        Please provide the URL(s)
      </div>

      {links.length > 0 && (
        <div className="space-y-2">
          {links.map((link, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-gray-1 border rounded-md px-2"
            >
              <div className="flex items-center font-medium text-sm py-2">
                <span className="border-r border-gray-3 pr-1 mr-4">
                  <FaLink />
                </span>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-7 hover:underline truncate max-w-[200px] sm:max-w-[300px]"
                >
                  {link}
                </a>
              </div>
              <button
                type="button"
                onClick={() => onRemoveLink(index)}
                className="text-danger text-sm font-semibold hover:font-[900] py-2"
                disabled={disabled}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaGlobe className="text-gray-5" />
          </div>
          <input
            type="text"
            placeholder="Enter website or social media URL"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className=" h-full font-medium text-sm leading-4 text-gray-7 pl-10 border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent w-full"
            disabled={disabled}
          />
        </div>
        <button
          type="button"
          onClick={handleAddLink}
          className={`text-white px-4 py-2 rounded-lg ${
            disabled ? "bg-gray-4" : "bg-gray-7"
          } w-full sm:w-auto`}
          disabled={disabled}
        >
          Add URL
        </button>
      </div>
    </div>
  );
};

export default DigitalCaseLinksInput;
